import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Trophy, Crown, Users, Clock, MapPin, GameController,
  Play, CheckCircle, X, UserMinus
} from '@phosphor-icons/react';
import GamePill from '../components/GamePill';
import { useLogoFlash } from '../components/LogoFlash';

const API = process.env.REACT_APP_BACKEND_URL;

function BracketMatch({ match, isHost, onRecord, isFinal, roundIdx, totalRounds }) {
  const [showPicker, setShowPicker] = useState(false);
  const [selected, setSelected] = useState(null);

  const p1Won = match.winner?.id === match.player1?.id;
  const p2Won = match.winner?.id === match.player2?.id;
  const completed = match.status === 'completed';
  const pending = match.status === 'pending';
  const isBye = match.player1 && !match.player2;

  const handleConfirm = () => {
    if (selected) {
      onRecord(match.match_id, selected);
      setShowPicker(false);
      setSelected(null);
    }
  };

  return (
    <div className={`relative rounded-lg border transition-all ${
      completed
        ? isFinal ? 'border-[#FFD700]/30 bg-[#FFD700]/[0.03] shadow-[0_0_12px_rgba(255,215,0,0.06)]' : 'border-[#39FF14]/20 bg-[#39FF14]/[0.03]'
        : pending && match.player1 && match.player2
          ? 'border-[#9D4CDD]/30 bg-[#9D4CDD]/[0.03] shadow-[0_0_8px_rgba(157,76,221,0.06)]'
          : 'border-white/[0.08] bg-[#0c0c14]'
    }`} data-testid={`bracket-match-${match.match_id}`}>
      {/* Match number badge */}
      <div className="absolute -top-2 -left-1 text-[8px] text-[#444] font-mono bg-[#0a0a12] px-1 rounded">
        M{match.match_id}
      </div>
      {/* Player 1 */}
      <div className={`flex items-center gap-2 px-3 py-2.5 border-b border-white/[0.05] transition-colors ${
        p1Won ? 'bg-[#39FF14]/[0.08]' : ''
      }`}>
        <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-bold ${
          match.player1 ? (p1Won ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#9D4CDD]/20 text-[#9D4CDD]') : 'bg-white/[0.05] text-[#333]'
        }`}>
          {match.player1?.seed || '-'}
        </div>
        <span className={`text-[12px] flex-1 truncate transition-colors ${
          p1Won ? 'text-[#39FF14] font-bold' : match.player1 ? 'text-white font-medium' : 'text-[#444] italic'
        }`}>
          {match.player1?.name || 'TBD'}
        </span>
        {p1Won && <Trophy size={12} weight="fill" className="text-[#FFD700] flex-shrink-0" />}
      </div>
      {/* Versus divider */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[7px] text-[#333] font-bold tracking-wider">VS</div>
      {/* Player 2 */}
      <div className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${
        p2Won ? 'bg-[#39FF14]/[0.08]' : ''
      }`}>
        <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-bold ${
          match.player2 ? (p2Won ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#00F0FF]/20 text-[#00F0FF]') : 'bg-white/[0.05] text-[#333]'
        }`}>
          {match.player2?.seed || '-'}
        </div>
        <span className={`text-[12px] flex-1 truncate transition-colors ${
          p2Won ? 'text-[#39FF14] font-bold' : match.player2 ? 'text-white font-medium' : 'text-[#444] italic'
        }`}>
          {isBye ? 'BYE' : (match.player2?.name || 'TBD')}
        </span>
        {p2Won && <Trophy size={12} weight="fill" className="text-[#FFD700] flex-shrink-0" />}
      </div>
      {/* Record button */}
      {isHost && pending && match.player1 && match.player2 && (
        <button onClick={() => setShowPicker(true)}
          className="absolute -right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#9D4CDD] flex items-center justify-center shadow-lg shadow-[#9D4CDD]/20 hover:bg-[#8B3DC9] transition-all hover:scale-110 active:scale-95 z-10"
          data-testid={`record-${match.match_id}`}>
          <Play size={12} weight="fill" className="text-white ml-0.5" />
        </button>
      )}
      {/* Winner picker */}
      <AnimatePresence>
        {showPicker && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0c0c14] border border-white/[0.1] rounded-lg p-2 shadow-xl">
            <p className="text-[10px] text-[#555] mb-1.5 font-medium">Select winner:</p>
            {[match.player1, match.player2].filter(Boolean).map(p => (
              <button key={p.id} onClick={() => setSelected(p.id)}
                className={`w-full px-2.5 py-2 rounded-lg text-left text-[12px] mb-1 flex items-center justify-between transition-all ${
                  selected === p.id ? 'bg-[#39FF14]/15 text-white border border-[#39FF14]/30' : 'text-[#888] hover:text-white hover:bg-white/[0.04] border border-transparent'
                }`}>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded text-[8px] flex items-center justify-center bg-white/[0.06] font-bold">{p.seed}</span>
                  {p.name}
                </span>
                {selected === p.id && <CheckCircle size={14} className="text-[#39FF14]" />}
              </button>
            ))}
            <div className="flex gap-1.5 mt-2">
              <button onClick={() => { setShowPicker(false); setSelected(null); }}
                className="flex-1 py-1.5 rounded-lg text-[11px] text-[#555] bg-white/[0.04] hover:text-white transition-colors">Cancel</button>
              <button onClick={handleConfirm} disabled={!selected}
                className="flex-1 py-1.5 rounded-lg text-[11px] text-white bg-[#9D4CDD] hover:bg-[#8B3DC9] disabled:opacity-30 transition-colors font-medium">Confirm Win</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function VisualBracket({ bracket, isHost, onRecord }) {
  if (!bracket || !bracket.matches || bracket.matches.length === 0) return null;

  const rounds = bracket.total_rounds || 0;
  const roundLabels = Array.from({ length: rounds }, (_, i) =>
    i === rounds - 1 ? 'Final' : i === rounds - 2 ? 'Semi-Final' : `Round ${i + 1}`
  );

  // Calculate round progress
  const roundProgress = Array.from({ length: rounds }, (_, i) => {
    const roundMatches = bracket.matches.filter(m => m.round === i + 1);
    const completed = roundMatches.filter(m => m.status === 'completed').length;
    return { total: roundMatches.length, completed, pct: roundMatches.length ? Math.round((completed / roundMatches.length) * 100) : 0 };
  });

  const champion = bracket.matches.find(m => m.round === rounds && m.status === 'completed')?.winner;

  return (
    <div data-testid="visual-bracket">
      {/* Round Progress Bar */}
      <div className="flex items-center gap-1 mb-5 px-1">
        {roundProgress.map((rp, i) => (
          <div key={i} className="flex-1 flex items-center gap-1.5">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold shrink-0 ${
              rp.pct === 100 ? 'bg-[#39FF14]/20 text-[#39FF14]' : rp.completed > 0 ? 'bg-[#9D4CDD]/20 text-[#9D4CDD]' : 'bg-white/[0.06] text-[#444]'
            }`}>
              {rp.pct === 100 ? <CheckCircle size={10} weight="fill" /> : i + 1}
            </div>
            {i < rounds - 1 && (
              <div className="flex-1 h-0.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${rp.pct}%`,
                  backgroundColor: rp.pct === 100 ? '#39FF14' : '#9D4CDD',
                }} />
              </div>
            )}
          </div>
        ))}
        {champion && (
          <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center shrink-0">
            <Crown size={10} weight="fill" className="text-[#FFD700]" />
          </div>
        )}
      </div>

      {/* Bracket Grid */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-8 min-w-max items-start">
          {Array.from({ length: rounds }, (_, roundIdx) => {
            const roundMatches = bracket.matches.filter(m => m.round === roundIdx + 1);
            const spacing = Math.pow(2, roundIdx);
            const rp = roundProgress[roundIdx];

            return (
              <div key={roundIdx} className="flex flex-col" style={{ minWidth: '190px' }}>
                {/* Round header */}
                <div className="mb-3 text-center">
                  <div className={`text-[10px] uppercase tracking-[0.15em] font-bold mb-1 ${
                    roundIdx === rounds - 1 ? 'text-[#FFD700]' : rp.pct === 100 ? 'text-[#39FF14]' : 'text-[#555]'
                  }`}>
                    {roundLabels[roundIdx]}
                  </div>
                  <div className="text-[9px] text-[#333]">{rp.completed}/{rp.total} matches</div>
                </div>

                <div className="flex flex-col justify-around flex-1 relative" style={{ gap: `${spacing * 20}px`, paddingTop: `${(spacing - 1) * 28}px` }}>
                  {roundMatches.map((match, matchIdx) => (
                    <div key={match.match_id} className="relative">
                      <BracketMatch match={match} isHost={isHost} onRecord={onRecord} isFinal={roundIdx === rounds - 1} roundIdx={roundIdx} totalRounds={rounds} />
                      {/* Elbow connectors to next round */}
                      {roundIdx < rounds - 1 && (
                        <>
                          {/* Horizontal line from match to midpoint */}
                          <div className="absolute top-1/2 -right-8 w-8 border-t border-white/[0.08]" />
                          {/* Vertical connector (only on even-indexed matches) */}
                          {matchIdx % 2 === 0 && matchIdx + 1 < roundMatches.length && (
                            <div className="absolute -right-8 border-r border-white/[0.08]" style={{
                              top: '50%',
                              height: `${spacing * 20 + 56}px`,
                            }} />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Champion Display */}
          {champion && (
            <div className="flex flex-col items-center justify-center" style={{ minWidth: '140px' }}>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#FFD700] font-bold mb-3">Champion</div>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="bg-gradient-to-b from-[#FFD700]/10 to-[#FFD700]/[0.03] border border-[#FFD700]/30 rounded-xl p-5 text-center shadow-[0_0_20px_rgba(255,215,0,0.08)]"
              >
                <div className="w-12 h-12 rounded-full bg-[#FFD700]/15 flex items-center justify-center mx-auto mb-2">
                  <Crown size={24} weight="fill" className="text-[#FFD700]" />
                </div>
                <p className="text-[14px] text-white font-bold">{champion.name}</p>
                <p className="text-[10px] text-[#FFD700]/60 mt-1">Seed #{champion.seed}</p>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TournamentDetailPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { flash, FlashRenderer } = useLogoFlash();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const intervalRef = useRef(null);

  const fetchTournament = async () => {
    try {
      const r = await axios.get(`${API}/api/tournaments/${tournamentId}`, { withCredentials: true });
      setTournament(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetchTournament();
    // Auto-refresh every 5 seconds during active tournaments
    intervalRef.current = setInterval(fetchTournament, 5000);
    return () => clearInterval(intervalRef.current);
  }, [tournamentId]);

  const handleJoin = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/tournaments/${tournamentId}/join`, {}, { withCredentials: true });
      flash({ message: 'Registered!', tone: 'success' });
      fetchTournament();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleLeave = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/tournaments/${tournamentId}/leave`, {}, { withCredentials: true });
      fetchTournament();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleStart = async () => {
    try {
      await axios.post(`${API}/api/tournaments/${tournamentId}/start`, {}, { withCredentials: true });
      flash({ message: 'Bracket Live!', tone: 'celebration' });
      fetchTournament();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleRecordMatch = async (matchId, winnerId) => {
    try {
      await axios.post(`${API}/api/tournaments/${tournamentId}/match`, { match_id: matchId, winner_id: winnerId }, { withCredentials: true });
      fetchTournament();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return (
    <div className="max-w-5xl mx-auto flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!tournament) return null;

  const bracket = tournament.bracket || {};
  const isInProgress = tournament.status === 'in_progress';
  const isCompleted = tournament.status === 'completed';
  const isRegistrationOpen = tournament.status === 'registration';

  // Determine if current user is host/participant
  let isHost = false;
  let isParticipant = false;
  try {
    const cookies = document.cookie;
    // We'll check via tournament data
    isParticipant = tournament.participants?.some(p => true) || false; // simplified
  } catch {}
  // Use host_id comparison — we don't have current user ID easily in this component, so we use the action buttons conditionally
  const showHostControls = true; // Server will reject if not host

  return (
    <div className="max-w-5xl mx-auto" data-testid="tournament-detail-page">
      <FlashRenderer />
      {/* Header */}
      <button onClick={() => navigate('/tournaments')} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back to Tournaments
      </button>

      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <GamePill game={tournament.game_type} />
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                tournament.status === 'registration' ? 'text-[#39FF14] bg-[#39FF14]/10' :
                tournament.status === 'in_progress' ? 'text-[#00F0FF] bg-[#00F0FF]/10' :
                tournament.status === 'completed' ? 'text-[#FFD700] bg-[#FFD700]/10' :
                'text-[#71717A] bg-[#71717A]/10'
              }`}>{tournament.status.replace('_', ' ')}</span>
              {/* Live indicator */}
              {isInProgress && (
                <span className="flex items-center gap-1 text-[10px] text-[#EF4444]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" /> LIVE
                </span>
              )}
            </div>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>{tournament.name}</h1>
            {tournament.winner_name && (
              <div className="flex items-center gap-2 mt-2 text-[#FFD700]">
                <Crown size={18} weight="fill" /> <span className="font-semibold text-[14px]">Champion: {tournament.winner_name}</span>
              </div>
            )}
          </div>
          <div className="text-right text-[12px] text-[#555]">
            <div className="flex items-center gap-1 justify-end"><Clock size={13} className="text-[#00F0FF]" /> {tournament.date} {tournament.time}</div>
            <div className="flex items-center gap-1 justify-end mt-1"><Users size={13} className="text-[#39FF14]" /> {tournament.current_players}/{tournament.max_players}</div>
            <div className="flex items-center gap-1 justify-end mt-1"><MapPin size={13} className="text-[#9D4CDD]" /> {tournament.location}</div>
          </div>
        </div>
        {tournament.description && <p className="text-[12px] text-[#666] mt-3">{tournament.description}</p>}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mb-4">
        {isRegistrationOpen && (
          <>
            <button onClick={handleJoin} disabled={actionLoading}
              className="flex-1 h-9 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] text-[12px] font-medium hover:bg-[#39FF14]/20 transition-colors"
              data-testid="join-tournament">Register</button>
            <button onClick={handleLeave} disabled={actionLoading}
              className="h-9 px-4 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[#888] text-[12px] hover:text-white transition-colors"
              data-testid="leave-tournament"><UserMinus size={14} /></button>
          </>
        )}
        {isRegistrationOpen && tournament.current_players >= 2 && (
          <button onClick={handleStart}
            className="flex-1 h-9 rounded-lg bg-[#9D4CDD] text-white text-[12px] font-medium flex items-center justify-center gap-1 hover:bg-[#8B3DC9] transition-colors"
            data-testid="start-tournament">
            <Play size={14} weight="fill" /> Start Tournament
          </button>
        )}
      </div>

      {/* Visual Bracket */}
      {(isInProgress || isCompleted) && bracket.total_rounds > 0 && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[14px] font-bold text-white flex items-center gap-2">
              <Trophy size={16} weight="duotone" className="text-[#FFD700]" /> Tournament Bracket
            </h2>
            <div className="flex items-center gap-3">
              {isInProgress && (
                <span className="flex items-center gap-1.5 text-[10px] text-[#EF4444] bg-[#EF4444]/10 px-2 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" /> LIVE
                </span>
              )}
              {isCompleted && (
                <span className="flex items-center gap-1 text-[10px] text-[#39FF14] bg-[#39FF14]/10 px-2 py-1 rounded-full">
                  <CheckCircle size={10} weight="fill" /> Complete
                </span>
              )}
              {isInProgress && <span className="text-[9px] text-[#444]">Refreshes every 5s</span>}
            </div>
          </div>
          <VisualBracket bracket={bracket} isHost={showHostControls} onRecord={handleRecordMatch} />
        </div>
      )}

      {/* Participants */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
          <Users size={16} weight="duotone" className="text-[#00F0FF]" /> Participants ({tournament.current_players}/{tournament.max_players})
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {tournament.participants?.map((p, idx) => (
            <div key={p.id} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
              tournament.winner_id === p.id
                ? 'bg-[#FFD700]/[0.06] border-[#FFD700]/20'
                : 'bg-white/[0.02] border-white/[0.04]'
            }`} data-testid={`participant-${p.id}`}>
              <div className="relative">
                {p.avatar ? (
                  <img src={p.avatar} alt={p.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-[11px] font-bold text-white">
                    {p.name?.charAt(0)}
                  </div>
                )}
                {/* Seed badge */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#0c0c14] border border-white/[0.1] flex items-center justify-center text-[7px] font-bold text-[#888]">
                  {idx + 1}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] text-white font-medium truncate">{p.name}</p>
                <p className="text-[10px] text-[#555]">
                  {tournament.winner_id === p.id && <span className="text-[#FFD700]">Champion</span>}
                  {tournament.winner_id !== p.id && `Rating: ${p.rating}`}
                </p>
              </div>
              {tournament.winner_id === p.id && <Crown size={14} weight="fill" className="text-[#FFD700] shrink-0" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
